import React from 'react'
import { useIntl } from 'react-intl'
import { Mutation } from 'react-apollo'
import { PureQueryOptions } from 'apollo-client'
import { DocumentNode } from 'graphql'
import gql from 'graphql-tag'
import { css } from '@emotion/css'
import * as deleteAppLibrariesByFiltersModule from '../../../../graphql/schema/mutations/delete-app-libraries-by-filters'
import {
  Modal,
  ModalBody,
  ModalFooter
} from '@contrast-security-inc/design-system-popovers'
import { Button } from '@contrast-security-inc/design-system-buttons'
import { toast } from '@contrast-security-inc/design-system-alerts'
import { RoleSwitch } from 'contrastBase/RoleSwitch'
import { Role } from '@contrast-security-inc/base-types'
import { theme } from '@contrast-security-inc/design-system-foundations'

export enum DeleteLibrariesType {
  BY_HASHES = 'BY_HASHES',
  BY_FILTERS = 'BY_FILTERS'
}

export enum FilterLibrariesActionType {
  BY_HASHES = 'BY_HASHES',
  BY_FILTERS = 'BY_FILTERS'
}

export enum DeleteLibrariesMode {
  APPLICATION = 'APPLICATIONS',
  SERVER = 'SERVERS',
  ORGANIZATION = 'ORGANIZATION'
}

interface Config {
  mutation: DocumentNode
  messaging: any
}

export const mainConfig: Record<
  DeleteLibrariesType,
  Record<DeleteLibrariesMode, Config>
> = {
  [DeleteLibrariesType.BY_HASHES]: {
    [DeleteLibrariesMode.ORGANIZATION]: {
      mutation: gql`
        mutation deleteOrgLibrariesByHashes(
          $organizationID: ID!
          $orgLibraryHashes: [String]!
        ) {
          deleteOrgLibrariesByHashes(
            input: {
              organizationID: $organizationID
              orgLibraryHashes: $orgLibraryHashes
            }
          ) {
            _placeholder
          }
        }
      `,
      messaging: {
        singular: 'variable_libraries_deleteLibraryGlobally_react',
        plural: 'variable_libraries_deleteLibrariesGlobally_react'
      }
    },
    [DeleteLibrariesMode.SERVER]: {
      mutation: gql`
        mutation deleteServerLibrariesByHashes(
          $organizationID: ID!
          $serverLibraryHashes: [String]!
          $serverID: String!
        ) {
          deleteServerLibrariesByHashes(
            input: {
              organizationID: $organizationID
              serverLibraryHashes: $serverLibraryHashes
              serverID: $serverID
            }
          ) {
            _placeholder
          }
        }
      `,
      messaging: {
        singular: 'variable_libraries_deleteLibraryFromAppOrServer_react',
        plural: 'variable_libraries_deleteLibrariesFromAppOrServer_react'
      }
    },
    [DeleteLibrariesMode.APPLICATION]: {
      mutation: gql`
        mutation deleteAppLibrariesByHashes(
          $organizationID: ID!
          $appLibraryHashes: [String]!
          $appID: String!
        ) {
          deleteAppLibrariesByHashes(
            input: {
              organizationID: $organizationID
              appLibraryHashes: $appLibraryHashes
              appID: $appID
            }
          ) {
            _placeholder
          }
        }
      `,
      messaging: {
        singular: 'variable_libraries_deleteLibraryFromAppOrServer_react',
        plural: 'variable_libraries_deleteLibrariesFromAppOrServer_react'
      }
    }
  },
  [DeleteLibrariesType.BY_FILTERS]: {
    [DeleteLibrariesMode.ORGANIZATION]: {
      mutation: gql`
        mutation deleteOrgLibrariesWithFilters(
          $organizationID: ID!
          $filters: LibraryFilterArg!
          $libraryHashesToExclude: [String]!
        ) {
          deleteOrgLibrariesWithFilters(
            input: {
              organizationID: $organizationID
              filters: $filters
              libraryHashesToExclude: $libraryHashesToExclude
            }
          ) {
            _placeholder
          }
        }
      `,
      messaging: 'action_bulkDeleteOrgLibrariesMatchingFilters'
    },
    [DeleteLibrariesMode.SERVER]: {
      mutation: gql`
        mutation deleteServerLibrariesWithFilters(
          $organizationID: ID!
          $filters: LibraryFilterArg!
          $libraryHashesToExclude: [String]!
          $serverID: String!
        ) {
          deleteServerLibrariesWithFilters(
            input: {
              organizationID: $organizationID
              filters: $filters
              libraryHashesToExclude: $libraryHashesToExclude
              serverID: $serverID
            }
          ) {
            _placeholder
          }
        }
      `,
      messaging: 'action_bulkDeleteAppOrServerLibrariesMatchingFilters'
    },
    [DeleteLibrariesMode.APPLICATION]: {
      mutation: deleteAppLibrariesByFiltersModule.mutation,
      messaging: 'action_bulkDeleteAppOrServerLibrariesMatchingFilters'
    }
  }
}

interface Filters {
  filterText: string
  quickFilter: string
  aggregations: Record<string, string[]>
}

export const determineMode = (applicationID?: string, serverID?: string) => {
  if (!!applicationID === false && !!serverID === false) {
    return DeleteLibrariesMode.ORGANIZATION
  } else if (!!serverID === false) {
    return DeleteLibrariesMode.APPLICATION
  }
  return DeleteLibrariesMode.SERVER
}

export const buildVariables = (
  deleteType: DeleteLibrariesType,
  deleteMode: DeleteLibrariesMode,
  {
    organizationID,
    libraryHashes,
    applicationID,
    serverID,
    filters,
    libraryHashesToExclude
  }: {
    libraryHashes?: string[]
    organizationID: string
    applicationID?: string
    serverID?: string
    filters?: Filters
    libraryHashesToExclude?: string[]
  }
) => {
  switch (deleteType) {
    case DeleteLibrariesType.BY_HASHES:
      switch (deleteMode) {
        case DeleteLibrariesMode.ORGANIZATION:
          return {
            organizationID,
            orgLibraryHashes: libraryHashes
          }
        case DeleteLibrariesMode.SERVER:
          return {
            organizationID,
            serverID,
            serverLibraryHashes: libraryHashes
          }
        case DeleteLibrariesMode.APPLICATION:
          return {
            organizationID,
            appID: applicationID,
            appLibraryHashes: libraryHashes
          }
      }
    case DeleteLibrariesType.BY_FILTERS:
      if (!filters) {
        throw new Error('filters are required')
      }
      const filterVariablesBase = {
        organizationID,
        filters: {
          filterText: filters.filterText,
          quickFilter: filters.quickFilter,
          ...filters.aggregations
        },
        libraryHashesToExclude
      }
      switch (deleteMode) {
        case DeleteLibrariesMode.ORGANIZATION:
          return filterVariablesBase
        case DeleteLibrariesMode.SERVER:
          return {
            ...filterVariablesBase,
            serverID
          }
        case DeleteLibrariesMode.APPLICATION:
          return {
            ...filterVariablesBase,
            applicationID
          }
      }
  }
}

interface BulkDeleteLibrariesProps {
  deleteType: DeleteLibrariesType
  applicationID?: string
  serverID?: string
  organizationID: string
  libraries?: Library[]
  onSuccess?: () => Promise<any> | (() => void)
  refetchQueries?: PureQueryOptions[]
  refresh?: () => Promise<any> | (() => void)
  showDisabled: boolean
  organizationRole: Role
  filters?: Filters
  libraryHashesToExclude?: string[]
  renderIcon?: boolean
  setShouldKeepPopOverOpen?: any
  onClose?: () => void
  setShowDeleteModal: (isOpen: boolean) => void
}

export function BulkDeleteLibrariesModal({
  deleteType,
  applicationID,
  serverID,
  filters,
  libraryHashesToExclude,
  organizationID,
  libraries = [],
  onSuccess,
  refetchQueries,
  organizationRole,
  onClose,
  setShowDeleteModal
}: BulkDeleteLibrariesProps) {
  const intl = useIntl()
  const deleteMode = determineMode(applicationID, serverID)
  const { mutation, messaging } = mainConfig[deleteType][deleteMode]

  return (
    <Mutation mutation={mutation}>
      {(deleteLibrariesFn) => {
        return (
          <BulkDeleteModal
            onClose={onClose}
            mutationFn={() => {
              return deleteLibrariesFn({
                refetchQueries,
                variables: buildVariables(deleteType, deleteMode, {
                  organizationID,
                  applicationID,
                  serverID,
                  libraryHashes: libraries.map(({ hash }) => hash),
                  filters,
                  libraryHashesToExclude
                })
              })
            }}
            onSuccess={onSuccess}
            organizationRole={organizationRole}
            renderBody={() => {
              switch (deleteType) {
                case DeleteLibrariesType.BY_HASHES:
                  const total = libraries.length
                  return total === 1
                    ? intl.formatMessage(
                        { id: messaging.singular },
                        { libraryName: <strong>{libraries[0].name}</strong> }
                      )
                    : intl.formatMessage(
                        { id: messaging.plural },
                        { librariesAmount: <strong>{total}</strong> }
                      )
                case DeleteLibrariesType.BY_FILTERS:
                  return intl.formatMessage({ id: messaging })
              }
            }}
            setShowDeleteModal={setShowDeleteModal}
          />
        )
      }}
    </Mutation>
  )
}

export interface Library {
  name: string
  hash: string
}

function BulkDeleteModal({
  mutationFn,
  organizationRole,
  onSuccess,
  renderBody,
  onClose,
  setShowDeleteModal
}: {
  mutationFn: () => Promise<any>
  onSuccess?: () => Promise<any> | (() => void)
  organizationRole: Role
  renderBody: () => any
  onClose?: () => void
  setShowDeleteModal: (isOpen: boolean) => void
}) {
  const { formatMessage } = useIntl()

  const handleClose = () => {
    setShowDeleteModal(false)
  }
  const handleConfirm = async () => {
    setShowDeleteModal(false)
    try {
      await mutationFn()

      if (onSuccess) {
        await onSuccess()
      }
      if (onClose) {
        onClose()
      }
    } catch (error) {
      console.error(error)
      toast(
        formatMessage({
          id: 'detail_libraries_failedToDeleteLibraries'
        }),
        { variant: 'error' }
      )
    }
  }

  return (
    <RoleSwitch
      role={organizationRole}
      requiredRole="EDIT"
      renderAllowed={() => (
        <Modal
          variant="confirmation"
          size="sm"
          isOpen={true}
          onClose={handleClose}
          title={formatMessage({ id: 'detail_deleteConfirmation' })}
          zIndex={theme.zIndex.popover + 1}
        >
          <ModalBody>
            <div
              className={css`
                margin-bottom: 15px;
              `}
            >
              {renderBody()}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              {formatMessage({ id: 'action_cancel' })}
            </Button>
            <Button
              onClick={handleConfirm}
              testId="delete-bulk-libraries-confirm-btn"
            >
              {formatMessage({ id: 'action_delete' })}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    />
  )
}
